/**
 * useBackendWorkerAnalysis Hook
 * 
 * Manages the backend worker analysis flow for module analysis.
 * Handles starting analysis, polling for status, and displaying progress.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BatchStats {
  queued: number;
  processing: number;
  completed: number;
  failed: number;
  total: number;
}

interface RevisionStatus {
  id: string;
  status: string;
  progress_percent: number;
  total_batches: number;
  processed_batches: number;
  total_tokens_in: number;
  total_tokens_out: number;
  total_cost_sek: number;
  last_error: string | null;
  created_at: string;
  completed_at: string | null;
}

interface UseBackendWorkerAnalysisOptions {
  auditId: string;
  moduleType: string;
  onComplete?: (revisionId: string) => void;
  onError?: (error: string) => void;
}

interface UseBackendWorkerAnalysisReturn {
  isRunning: boolean;
  revisionId: string | null;
  revisionStatus: RevisionStatus | null;
  batchStats: BatchStats | null;
  progress: number;
  error: string | null;
  startAnalysis: (params: StartAnalysisParams) => Promise<void>;
  cancelAnalysis: () => Promise<void>;
}

interface StartAnalysisParams {
  /** Single file path (legacy) or array of file paths (multi-file) */
  storagePath?: string;
  /** Array of storage paths for multi-file analysis */
  storagePaths?: string[];
  fieldMapping: Record<string, string>;
  companySize?: 'small' | 'large';
  vatReportingPeriod?: 'monthly' | 'quarterly';
  toleranceDays?: number;
  fileName?: string;
  companyName?: string;
  orgNumber?: string;
  /** Materiality thresholds for filtering findings */
  thresholds?: {
    clearly_trivial_threshold?: number;
    overall_materiality?: number;
    performance_materiality?: number;
  };
}

export function useBackendWorkerAnalysis({
  auditId,
  moduleType,
  onComplete,
  onError,
}: UseBackendWorkerAnalysisOptions): UseBackendWorkerAnalysisReturn {
  const { toast } = useToast();
  
  const [isRunning, setIsRunning] = useState(false);
  const [revisionId, setRevisionId] = useState<string | null>(null);
  const [revisionStatus, setRevisionStatus] = useState<RevisionStatus | null>(null);
  const [batchStats, setBatchStats] = useState<BatchStats | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Prevent duplicate completion toasts when both polling and realtime fire.
  const completionNotifiedForRevisionRef = useRef<string | null>(null);
  // Track if we just started an analysis to prevent useEffect from resetting state
  const justStartedRef = useRef(false);
  // Store callbacks in refs to avoid re-creating functions
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  
  // Keep refs updated
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  // Cleanup function - stable, no dependencies
  const cleanup = useCallback(() => {
    console.log('[useBackendWorkerAnalysis] Cleanup called');
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
  }, []);

  // Poll for revision status and batch stats - uses refs for callbacks
  const pollStatus = useCallback(async (revId: string) => {
    try {
      // Fetch revision status
      const { data: revision, error: revError } = await supabase
        .from('revisions')
        .select('*')
        .eq('id', revId)
        .single();

      if (revError) {
        console.error('[useBackendWorkerAnalysis] Error fetching revision:', revError);
        return;
      }

      if (revision) {
        console.log('[useBackendWorkerAnalysis] Poll result:', {
          status: revision.status,
          progress: revision.progress_percent,
          processed: revision.processed_batches,
          total: revision.total_batches,
        });
        
        setRevisionStatus(revision as RevisionStatus);
        setProgress(revision.progress_percent || 0);

        // Check if completed or failed
        if (revision.status === 'completed' || revision.status === 'aggregated') {
          // Always stop the UI when backend is done; only toast/callback once.
          setIsRunning(false);
          cleanup();

          if (completionNotifiedForRevisionRef.current !== revId) {
            completionNotifiedForRevisionRef.current = revId;
            toast({
              title: 'Analys slutförd',
              description: `Bearbetade ${revision.processed_batches || 0} batchar`,
            });
            onCompleteRef.current?.(revId);
          }
        } else if (revision.status === 'failed' || revision.status === 'cancelled') {
          setIsRunning(false);
          setError(revision.last_error || 'Analys misslyckades');
          cleanup();
          onErrorRef.current?.(revision.last_error || 'Analys misslyckades');
        }
      }

      // Fetch batch stats
      const { data: batches, error: batchError } = await supabase
        .from('batches')
        .select('state')
        .eq('revision_id', revId);

      if (!batchError && batches) {
        const stats: BatchStats = {
          queued: batches.filter(b => b.state === 'queued').length,
          processing: batches.filter(b => b.state === 'processing').length,
          completed: batches.filter(b => b.state === 'completed').length,
          failed: batches.filter(b => b.state === 'failed').length,
          total: batches.length,
        };
        setBatchStats(stats);
      }
    } catch (err) {
      console.error('[useBackendWorkerAnalysis] Poll error:', err);
    }
  }, [cleanup, toast]);

  // Setup realtime subscription - uses pollStatus
  const setupRealtimeSubscription = useCallback((revId: string) => {
    console.log('[useBackendWorkerAnalysis] Setting up realtime for:', revId);
    
    // Remove existing channel if any
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }
    
    const channel = supabase
      .channel(`revision-${revId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'revisions',
          filter: `id=eq.${revId}`,
        },
        (payload) => {
          const newData = payload.new as Record<string, unknown> | undefined;
          console.log('[useBackendWorkerAnalysis] Revision update:', newData?.status);
          // Let pollStatus handle state updates to avoid duplication
          pollStatus(revId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'batches',
          filter: `revision_id=eq.${revId}`,
        },
        () => {
          console.log('[useBackendWorkerAnalysis] Batch change detected');
          pollStatus(revId);
        }
      )
      .subscribe((status) => {
        console.log('[useBackendWorkerAnalysis] Realtime subscription status:', status);
      });

    realtimeChannelRef.current = channel;
  }, [pollStatus]);

  // Start analysis
  const startAnalysis = useCallback(async (params: StartAnalysisParams) => {
    // Mark that we just started to prevent useEffect from resetting
    justStartedRef.current = true;
    
    setError(null);
    setIsRunning(true);
    setProgress(0);
    setBatchStats(null);
    setRevisionStatus(null);
    completionNotifiedForRevisionRef.current = null;

    try {
      // Support both single file (legacy) and multi-file paths
      const fileIds = params.storagePaths || (params.storagePath ? [params.storagePath] : []);
      
      console.log('[useBackendWorkerAnalysis] Starting analysis with params:', {
        auditId,
        moduleType,
        fileIds,
        hasFieldMapping: !!params.fieldMapping,
      });

      const { data, error: invokeError } = await supabase.functions.invoke('start-backend-worker', {
        body: {
          fileIds: fileIds,  // Now always an array
          fieldMapping: params.fieldMapping,
          companySize: params.companySize || 'small',
          vatReportingPeriod: params.vatReportingPeriod || 'monthly',
          toleranceDays: params.toleranceDays ?? 0,
          fileName: params.fileName,
          companyName: params.companyName,
          orgNumber: params.orgNumber,
          auditId,
          moduleType,
          thresholds: params.thresholds,
        },
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Kunde inte starta analys');
      }

      if (!data?.success || !data?.revision_id) {
        throw new Error(data?.error || 'Ogiltig respons från backend worker');
      }

      console.log('[useBackendWorkerAnalysis] Analysis started:', data);
      
      setRevisionId(data.revision_id);
      
      const filesInfo = data.files_processed > 1 
        ? `${data.files_processed} filer sammanslagna, ` 
        : '';
      const rowsInfo = data.total_rows ? `${data.total_rows} rader, ` : '';
      
      toast({
        title: 'Analys startad',
        description: `${filesInfo}${rowsInfo}${data.batches_created} batchar skapade. Bearbetning pågår...`,
      });

      // Setup realtime subscription and polling
      setupRealtimeSubscription(data.revision_id);
      
      // Poll every 2 seconds as backup to realtime
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(() => {
        pollStatus(data.revision_id);
      }, 2000);

      // Initial poll
      pollStatus(data.revision_id);
      
      // Clear the "just started" flag after a delay
      setTimeout(() => {
        justStartedRef.current = false;
      }, 2000);

    } catch (err) {
      console.error('[useBackendWorkerAnalysis] Start error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Ett fel uppstod';
      setError(errorMessage);
      setIsRunning(false);
      justStartedRef.current = false;
      
      toast({
        title: 'Kunde inte starta analys',
        description: errorMessage,
        variant: 'destructive',
      });
      
      onErrorRef.current?.(errorMessage);
    }
  }, [auditId, moduleType, toast, setupRealtimeSubscription, pollStatus]);

  // Cancel analysis
  const cancelAnalysis = useCallback(async () => {
    if (!revisionId) return;

    try {
      await supabase
        .from('revisions')
        .update({
          status: 'cancelled',
          last_error: 'Avbruten av användare',
          completed_at: new Date().toISOString(),
        })
        .eq('id', revisionId);

      setIsRunning(false);
      cleanup();
      
      toast({
        title: 'Analys avbruten',
        description: 'Analysen har avbrutits',
      });
    } catch (err) {
      console.error('[useBackendWorkerAnalysis] Cancel error:', err);
    }
  }, [revisionId, cleanup, toast]);

  // Check for existing active revision on mount / when module changes
  // ONLY depends on auditId and moduleType to prevent unnecessary re-runs
  useEffect(() => {
    // Skip if we just started an analysis
    if (justStartedRef.current) {
      console.log('[useBackendWorkerAnalysis] Skipping reset - just started analysis');
      return;
    }
    
    let isMounted = true;
    console.log('[useBackendWorkerAnalysis] Effect running for:', { auditId, moduleType });

    // Cleanup previous subscriptions
    cleanup();
    
    // Reset state when audit/module context changes
    setIsRunning(false);
    setRevisionId(null);
    setRevisionStatus(null);
    setBatchStats(null);
    setProgress(0);
    setError(null);
    completionNotifiedForRevisionRef.current = null;

    const checkActiveRevision = async () => {
      if (!auditId || !moduleType) return;
      
      try {
        const { data: activeRevisions, error: queryError } = await supabase
          .from('revisions')
          .select('*')
          .eq('audit_id', auditId)
          .eq('module_type', moduleType)
          .in('status', ['initializing', 'batching', 'processing', 'aggregating', 'reporting'])
          .order('created_at', { ascending: false })
          .limit(1);

        if (queryError) {
          console.error('[useBackendWorkerAnalysis] Error checking active revision:', queryError);
          return;
        }

        if (!isMounted) return;

        if (activeRevisions && activeRevisions.length > 0) {
          const activeRevision = activeRevisions[0] as RevisionStatus;
          console.log('[useBackendWorkerAnalysis] Found active revision:', activeRevision.id, 'status:', activeRevision.status);

          setRevisionId(activeRevision.id);
          setRevisionStatus(activeRevision);
          setProgress(activeRevision.progress_percent || 0);
          setIsRunning(true);

          setupRealtimeSubscription(activeRevision.id);
          
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          pollingIntervalRef.current = setInterval(() => {
            pollStatus(activeRevision.id);
          }, 2000);
          
          // Initial poll
          pollStatus(activeRevision.id);
        } else {
          console.log('[useBackendWorkerAnalysis] No active revision found');
        }
      } catch (err) {
        console.error('[useBackendWorkerAnalysis] Error in checkActiveRevision:', err);
      }
    };

    checkActiveRevision();

    return () => {
      isMounted = false;
    };
  }, [auditId, moduleType]); // Only these two dependencies!

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isRunning,
    revisionId,
    revisionStatus,
    batchStats,
    progress,
    error,
    startAnalysis,
    cancelAnalysis,
  };
}
